/**
 * 盤面の拡大倍率の決定。
 *
 * **layout が決めるのは倍率だけで、盤面の寸法は決めない。**盤面の論理寸法は
 * 360 × 480 CSS px に固定されており（ADR-014、`core/constants.ts`）、`core` と `view` が扱う
 * 座標はすべてこの論理座標である。実表示はラッパ要素への `transform: scale()` で得る。
 *
 * この分離により、拡大倍率が変わってもピース座標の再計算が一切発生しない
 * （`04-architecture.md`「layout（view）」）。ポインタ座標だけが倍率の逆数で論理座標へ写される。
 *
 * 根拠: spec/02-requirements.md FR-004、spec/04-architecture.md layout（view）、ADR-014。
 */

import { BOARD_H, BOARD_W } from '../core/constants';

/** 論理座標系（360 × 480）上の点。 */
export interface LogicalPoint {
  readonly x: number;
  readonly y: number;
}

/** 盤面の視覚的な左上端のビューポート座標。`DOMRect` をそのまま渡せる。 */
export interface BoardOrigin {
  readonly left: number;
  readonly top: number;
}

/** 盤面ラッパに倍率を渡す CSS カスタムプロパティ名。 */
export const SCALE_PROPERTY = '--board-scale';

/**
 * 利用可能領域から盤面の拡大倍率を求める。
 *
 * `min(利用可能幅, 利用可能高 × 3/4) / 360`（FR-004）。3/4 は論理盤面のアスペクト比であり、
 * 定数から導く。高さ由来の項は `利用可能高 / 480` に等しいが、仕様の式をそのまま写している。
 *
 * 測定前・折りたたみ中などで領域が 0 以下や非有限になることがある。その場合は 0 を返し、
 * 呼び出し側は倍率の適用を見送る（0 を書き込むと盤面が消えるため）。
 */
export function computeScale(availableW: number, availableH: number): number {
  if (!Number.isFinite(availableW) || !Number.isFinite(availableH)) return 0;
  const usableW = Math.min(availableW, availableH * (BOARD_W / BOARD_H));
  return usableW > 0 ? usableW / BOARD_W : 0;
}

/**
 * ビューポート座標を論理座標へ写す。
 *
 * `origin` は `getBoundingClientRect()` の結果、すなわち `transform` 適用**後**の視覚的な矩形を
 * 想定する。差分を倍率で割ると論理座標が得られる。これを controller の手前で一度だけ行うことで、
 * `core` は倍率の存在を意識しない。
 */
export function toLogicalPoint(
  clientX: number,
  clientY: number,
  origin: BoardOrigin,
  scale: number,
): LogicalPoint {
  return { x: (clientX - origin.left) / scale, y: (clientY - origin.top) / scale };
}

/** 倍率の監視ハンドル。 */
export interface LayoutHandle {
  /** 監視を止める。 */
  readonly dispose: () => void;
  /** 現在の倍率。まだ測定できていなければ 0。 */
  readonly scale: () => number;
}

/**
 * `stage` の実サイズを監視し、盤面ラッパ `board` に倍率を書き込み続ける。
 *
 * `ResizeObserver` を使うのは、`resize` / `orientationchange` に加えて **URL バーの伸縮による
 * `100dvh` の変化**も同じ経路で拾えるためである（FR-004）。イベントを個別に購読すると、
 * dvh の変化だけがどのイベントにも現れない端末で盤面がはみ出す。
 *
 * 倍率が実際に変わったときだけ書き込む。ドラッグ中に無関係な再描画を誘発しないための配慮。
 */
export function observeLayout(
  stage: HTMLElement,
  board: HTMLElement,
  onChange?: (scale: number) => void,
): LayoutHandle {
  let current = 0;

  const update = (availableW: number, availableH: number): void => {
    const next = computeScale(availableW, availableH);
    if (next <= 0 || next === current) return;
    current = next;
    board.style.setProperty(SCALE_PROPERTY, String(next));
    onChange?.(next);
  };

  const observer = new ResizeObserver((entries) => {
    for (const entry of entries) {
      update(entry.contentRect.width, entry.contentRect.height);
    }
  });
  observer.observe(stage);

  // 初回は observer の初回通知を待たずに確定させる。待つと最初の 1 フレームだけ
  // 倍率未設定の盤面が見えてしまう。
  update(stage.clientWidth, stage.clientHeight);

  return {
    dispose: () => observer.disconnect(),
    scale: () => current,
  };
}
